import React, { useEffect, useRef, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { bool, func, object, string } from 'prop-types';
import { Button, Heading, Text } from '@landtrustinc/design-system';
import { isTooManyEmailVerificationRequestsError } from '../../util/errors';
import { sendVerificationEmail } from '../../ducks/user.duck';
import css from './InquiryEmailGate.module.css';

export const InquiryEmailGateComponent = ({
  currentUser,
  saved,
  landownerName,
  sendVerificationEmailInProgress,
  sendVerificationEmailError,
  onResendVerificationEmail,
}) => {
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);
  const resendStarted = useRef(false);
  const email = currentUser?.attributes?.email || '';
  const name = landownerName || 'the landowner';

  useEffect(() => {
    if (sendVerificationEmailInProgress) {
      resendStarted.current = true;
      setVerificationEmailSent(false);
      return;
    }
    if (resendStarted.current && !sendVerificationEmailError) {
      setVerificationEmailSent(true);
      resendStarted.current = false;
    }
  }, [sendVerificationEmailInProgress, sendVerificationEmailError]);

  const resendErrorId = isTooManyEmailVerificationRequestsError(sendVerificationEmailError)
    ? 'InquiryWizard.resendVerificationTooMany'
    : 'InquiryWizard.resendVerificationFailed';

  return (
    <div className={css.root} data-testid="inquiry-email-gate">
      <Heading as="h1" size="2xs" fontWeight="extrabold" color="var(--color-primary-800)">
        <FormattedMessage id="InquiryWizard.verifyEmailTitle" />
      </Heading>
      <Text size="md" color="var(--color-hunter-500)">
        <FormattedMessage
          id="InquiryWizard.verifyEmailBody"
          values={{
            email: email || 'your email',
            name,
          }}
        />
      </Text>
      {saved ? (
        <Text size="sm" fontWeight="bold" color="var(--color-primary-800)">
          <FormattedMessage id="InquiryWizard.tripDetailsSaved" />
        </Text>
      ) : null}
      {sendVerificationEmailError ? (
        <div data-testid="resend-verification-error">
          <Text size="sm" fontWeight="bold" color="var(--text-error)">
            <FormattedMessage id={resendErrorId} />
          </Text>
        </div>
      ) : null}
      {verificationEmailSent ? (
        <div data-testid="verification-email-sent">
          <Text size="sm" fontWeight="bold" color="var(--color-primary-800)">
            <FormattedMessage id="InquiryWizard.verificationEmailSent" />
          </Text>
        </div>
      ) : null}
      <Button
        variant="primary"
        type="button"
        fullWidth
        data-testid="resend-verification-email"
        disabled={sendVerificationEmailInProgress}
        isLoading={sendVerificationEmailInProgress}
        onClick={() => {
          if (sendVerificationEmailInProgress) {
            return;
          }
          onResendVerificationEmail();
        }}
      >
        <FormattedMessage id="InquiryWizard.resendVerificationEmail" />
      </Button>
    </div>
  );
};

InquiryEmailGateComponent.defaultProps = {
  currentUser: null,
  saved: false,
  landownerName: '',
  sendVerificationEmailInProgress: false,
  sendVerificationEmailError: null,
};

InquiryEmailGateComponent.propTypes = {
  currentUser: object,
  saved: bool,
  landownerName: string,
  sendVerificationEmailInProgress: bool,
  sendVerificationEmailError: object,
  onResendVerificationEmail: func.isRequired,
};

const mapStateToProps = state => {
  const { sendVerificationEmailInProgress, sendVerificationEmailError } = state.user;
  return {
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
  };
};

const mapDispatchToProps = dispatch => ({
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
});

const InquiryEmailGate = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(InquiryEmailGateComponent);

export default InquiryEmailGate;
